import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle } from "@/components/ui/sheet";
import { LogOut, LayoutDashboard, Plus, Globe, CreditCard, LineChart, Menu } from "lucide-react";

export function Navbar() {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const [ent, setEnt] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (user) api.get("/entitlement").then((r) => setEnt(r.data)).catch((e) => { console.error("entitlement fetch failed", e); setEnt(null); });
    else setEnt(null);
  }, [user]);

  const badge = ent && (ent.plan_active
    ? { text: ent.plan.toUpperCase(), cls: "bg-emerald-50 text-emerald-700 border-emerald-200" }
    : ent.credits > 0
      ? { text: `${ent.credits} ${t("ent_credits_badge")}`, cls: "bg-blue-950 text-blue-300 border-blue-800" }
      : { text: t("ent_no_plan"), cls: "bg-slate-800 text-slate-300 border-slate-700" });

  const toggleLang = () => setLang(lang === "it" ? "en" : "it");
  const goAndClose = (path) => { setMobileOpen(false); navigate(path); };
  const doLogout = async () => { setMobileOpen(false); await logout(); navigate("/login"); };

  return (
    <header className="no-print sticky top-0 z-40 bg-slate-900/80 backdrop-blur-xl border-b border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to={user ? "/dashboard" : "/"} data-testid="nav-home-link">
          <Logo size="sm" />
        </Link>

        {/* Desktop navigation */}
        <div className="hidden md:flex items-center gap-2 sm:gap-3">
          <Link to="/pricing" data-testid="nav-pricing-link"
            className="text-sm font-medium text-slate-300 hover:text-white px-2.5 py-1.5 rounded-md hover:bg-slate-800 transition-colors duration-200">
            {t("nav_pricing")}
          </Link>
          <button
            data-testid="lang-toggle"
            onClick={toggleLang}
            className="flex items-center gap-1.5 text-sm font-medium text-slate-300 hover:text-white px-2.5 py-1.5 rounded-md hover:bg-slate-800 transition-colors duration-200"
          >
            <Globe className="h-4 w-4" /> {lang.toUpperCase()}
          </button>
          {user ? (
            <>
              {badge && (
                <Link to="/pricing" data-testid="entitlement-badge"
                  className={`hidden lg:inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-full border ${badge.cls}`}>
                  <CreditCard className="h-3.5 w-3.5" /> {badge.text}
                </Link>
              )}
              <Button variant="ghost" size="sm" data-testid="nav-dashboard-btn"
                onClick={() => navigate("/dashboard")}>
                <LayoutDashboard className="h-4 w-4 mr-1.5" /> {t("nav_dashboard")}
              </Button>
              <Button variant="ghost" size="sm" data-testid="nav-bp-btn"
                onClick={() => navigate("/business-plan/new")} className="hidden lg:inline-flex">
                <LineChart className="h-4 w-4 mr-1.5" /> {t("bp_nav")}
              </Button>
              <Button size="sm" data-testid="nav-new-btn" onClick={() => navigate("/analysis/new")}>
                <Plus className="h-4 w-4 mr-1.5" /> {t("nav_new")}
              </Button>
              <Button variant="outline" size="sm" data-testid="nav-logout-btn"
                onClick={doLogout}>
                <LogOut className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <Button size="sm" data-testid="nav-login-btn" onClick={() => navigate("/login")}>
              {t("nav_login")}
            </Button>
          )}
        </div>

        {/* Mobile navigation */}
        <div className="flex md:hidden items-center gap-1">
          <button
            data-testid="lang-toggle-mobile"
            onClick={toggleLang}
            className="flex items-center gap-1 text-sm font-medium text-slate-300 hover:text-white px-2 py-1.5 rounded-md hover:bg-slate-800 transition-colors duration-200"
          >
            <Globe className="h-4 w-4" /> {lang.toUpperCase()}
          </button>
          {user ? (
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm" data-testid="nav-mobile-menu-btn" className="px-2">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 bg-slate-950 border-slate-800 p-0">
                <SheetTitle className="sr-only">Menu</SheetTitle>
                <div className="flex flex-col h-full">
                  <div className="px-5 py-5 border-b border-slate-800">
                    <Logo size="sm" />
                  </div>
                  {badge && (
                    <button onClick={() => goAndClose("/pricing")} data-testid="entitlement-badge-mobile"
                      className={`mx-5 mt-4 inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-full border w-fit ${badge.cls}`}>
                      <CreditCard className="h-3.5 w-3.5" /> {badge.text}
                    </button>
                  )}
                  <nav className="flex flex-col gap-1 px-3 py-4">
                    <MobileItem icon={LayoutDashboard} label={t("nav_dashboard")} onClick={() => goAndClose("/dashboard")} testid="nav-mobile-dashboard" />
                    <MobileItem icon={Plus} label={t("nav_new")} onClick={() => goAndClose("/analysis/new")} testid="nav-mobile-new" />
                    <MobileItem icon={LineChart} label={t("bp_nav")} onClick={() => goAndClose("/business-plan/new")} testid="nav-mobile-bp" />
                    <MobileItem icon={CreditCard} label={t("nav_pricing")} onClick={() => goAndClose("/pricing")} testid="nav-mobile-pricing" />
                  </nav>
                  <div className="mt-auto px-3 pb-6 pt-2 border-t border-slate-800">
                    <MobileItem icon={LogOut} label={t("nav_logout")} onClick={doLogout} testid="nav-mobile-logout" danger />
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          ) : (
            <>
              <Link to="/pricing" data-testid="nav-pricing-link-mobile"
                className="text-sm font-medium text-slate-300 hover:text-white px-2 py-1.5 rounded-md hover:bg-slate-800 transition-colors duration-200">
                {t("nav_pricing")}
              </Link>
              <Button size="sm" data-testid="nav-login-btn-mobile" onClick={() => navigate("/login")}>
                {t("nav_login")}
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

function MobileItem({ icon: Icon, label, onClick, testid, danger }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`flex items-center gap-3 w-full text-left px-3 py-3 rounded-lg text-sm font-medium transition-colors duration-150 ${danger ? "text-red-400 hover:bg-red-500/10" : "text-slate-200 hover:bg-slate-800"}`}>
      <Icon className="h-4.5 w-4.5 shrink-0" style={{ width: 18, height: 18 }} /> {label}
    </button>
  );
}
